import { useCallback, useMemo } from 'react';

import type { Grouping } from '@bugbug/core/types/groups';
import type { Project } from '@bugbug/core/types/projects';
import type { Test } from '@bugbug/core/types/tests';
import useAppRoutes from '~/hooks/useAppRoutes';
import { selectCurrentOrganizationId } from '~/modules/organization/organization.selectors';
import { selectSingleProject } from '~/modules/project/project.selectors';
import { useAppSelector } from '~/modules/store';
import { selectHasNotCreatedTest } from '~/modules/user/user.selectors';
import analytics, { TRACK_EVENT_ARG_TYPE } from '~/services/analytics';
import { ACTIONS } from '~/services/toasts/internalServerError';

import { testsApi } from '../../tests.api';
import { selectTests } from '../../tests.selectors';
import { showTestError } from '../../tests.utils';

export const useCreateTest = () => {
  const project = useAppSelector(selectSingleProject) as Project;
  const organizationId = useAppSelector(selectCurrentOrganizationId);
  const testsList = useAppSelector(selectTests);
  const hasNotCreatedTest = useAppSelector(selectHasNotCreatedTest);
  const appRoutes = useAppRoutes('test');
  const [createTest, createTestRequest] = testsApi.useCreateTestMutation();

  const handleCreateTest = useCallback(
    async (
      params: Pick<Test, 'name' | 'runProfileId' | 'screenSizeType'> & {
        group?: { id: Grouping['id']; testId: Test['id'] };
        projectId?: Project['id'];
        projectSlug?: Project['slug'];
      },
    ) => {
      if (hasNotCreatedTest) {
        analytics.trackEvent('first_test_created');
      }
      analytics.trackEvent('test_created', {
        [TRACK_EVENT_ARG_TYPE.TEST_NAME]: params.name,
        [TRACK_EVENT_ARG_TYPE.TESTS_COUNT]: (testsList?.length ?? 0) + 1,
      });

      const projectId = params.projectId ?? project.id;
      const projectSlug = params.projectSlug ?? project.slug;

      const result = createTest({
        data: params,
        projectId,
      }).unwrap();

      result
        .then((createdTest) => {
          analytics.trackEvent('test_opened');

          appRoutes.push('test', {
            testId: createdTest.id,
            projectId,
            projectSlug,
            organizationId,
          });
        })
        .catch((error) => {
          showTestError(ACTIONS.CREATE, error);
        });

      return result;
    },
    [
      appRoutes,
      createTest,
      hasNotCreatedTest,
      organizationId,
      project.id,
      project.slug,
      testsList?.length,
    ],
  );

  return useMemo(
    () => [handleCreateTest, createTestRequest] as const,
    [handleCreateTest, createTestRequest],
  );
};
